import {
  fillBlock,
  fillText,
  numberInCell,
  spanCoversFigure as coversFigure,
  spellLedgerValue,
  withTileUnit,
  type TFillRef
} from "@libs/figure-spans";
import { fromLegacyBlock } from "@/zapi-frontend/shared/lib/contentBlocks";
import type { TFigureRef, THeadlineItem } from "@/zapi-frontend/shared/types/contentBlocks";

export const FIGURE_KEY_ATTR = "data-figure-key";

export type TFigureValue = { value: number | null; unit?: string | null; dp?: number | null };
export type TFigureLookup = Map<string, TFigureValue> | { get: (key: string) => TFigureValue | undefined };

const escapeAttr = (value: string) => value.replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

export const spanCoversFigure = (text: string, ref: TFigureRef): boolean =>
  ref.start !== undefined && ref.len !== undefined && coversFigure(text, ref.start, ref.len);

// Wraps every drawable span of a markdown text in a trigger the sanitizer lets through, right to
// left so an earlier insertion cannot move a later ref's offsets. Overlapping spans keep the first.
export const markFigureSpans = (text: string, refs: TFigureRef[] | undefined, figures: TFigureLookup): string => {
  const spans = (refs ?? [])
    .filter((ref) => figures.get(ref.key) !== undefined && spanCoversFigure(text, ref))
    .sort((a, b) => (a.start as number) - (b.start as number));
  const kept: TFigureRef[] = [];
  let lastEnd = -1;
  for (const ref of spans) {
    const start = ref.start as number;
    if (start < lastEnd) continue;
    kept.push(ref);
    lastEnd = start + (ref.len as number);
  }
  let out = text;
  for (const ref of kept.reverse()) {
    const start = ref.start as number;
    const end = start + (ref.len as number);
    const open = `<span ${FIGURE_KEY_ATTR}="${escapeAttr(ref.key)}" tabindex="0" aria-haspopup="dialog">`;
    out = `${out.slice(0, start)}${open}${out.slice(start, end)}</span>${out.slice(end)}`;
  }
  return out;
};

export type TTextPart = { text: string; key?: string };

// The same spans as `markFigureSpans`, for a text React renders itself rather than through markdown.
export const splitFigureSpans = (text: string, refs: TFigureRef[] | undefined, figures: TFigureLookup): TTextPart[] => {
  const spans = (refs ?? [])
    .filter((ref) => figures.get(ref.key) !== undefined && spanCoversFigure(text, ref))
    .sort((a, b) => (a.start as number) - (b.start as number));
  const parts: TTextPart[] = [];
  let at = 0;
  for (const ref of spans) {
    const start = ref.start as number;
    if (start < at) continue;
    if (start > at) parts.push({ text: text.slice(at, start) });
    at = start + (ref.len as number);
    parts.push({ text: text.slice(start, at), key: ref.key });
  }
  if (at < text.length) parts.push({ text: text.slice(at) });
  return parts;
};

export const fillFigureText = (
  text: string,
  refs: TFillRef[] | null | undefined,
  figures: TFigureLookup | null | undefined
): { text: string; refs: TFillRef[] } =>
  refs?.length
    ? fillText(
        text,
        refs,
        (key) => figures?.get(key)?.value,
        (key) => figures?.get(key)?.unit,
        (key) => figures?.get(key)?.dp
      )
    : { text, refs: [] };

const isRecord = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const refsAt = <T extends { refs?: unknown }>(
  holder: T | null | undefined,
  path: string
): TFillRef[] | undefined => {
  const refs = isRecord(holder?.refs) ? holder.refs[path] : undefined;
  return Array.isArray(refs) && refs.length > 0 ? (refs as TFillRef[]) : undefined;
};

export const holdsFigureSlots = (value: unknown): boolean =>
  Array.isArray(value)
    ? value.some(holdsFigureSlots)
    : isRecord(value) &&
      Object.entries(value).some(([field, inner]) =>
        field === "refs" && isRecord(inner)
          ? Object.values(inner).some((refs) => Array.isArray(refs) && refs.length > 0)
          : holdsFigureSlots(inner)
      );

export const refForItem = (refs: TFigureRef[] | undefined, item: number): TFigureRef | undefined =>
  refs?.find((ref) => ref.item === item);

export const refForCell = (refs: TFigureRef[] | undefined, row: number, col: number): TFigureRef | undefined =>
  refs?.find((ref) => ref.row === row && ref.col === col);

type TFillFigure = { key: string; value: number | null; unit?: string | null; dp?: number | null };

const sectionsOf = (content: string): { blocks?: unknown[] }[] | null => {
  try {
    const sections: unknown = JSON.parse(content);
    return Array.isArray(sections) ? (sections as { blocks?: unknown[] }[]) : null;
  } catch {
    return null;
  }
};

export const fillChapterContent = (content: string, figures: TFillFigure[] | undefined) => {
  const sections = sectionsOf(content);
  if (!sections) return content;
  const byKey = new Map((figures ?? []).map((figure) => [figure.key, figure]));
  const filled = sections.map((section) =>
    Array.isArray(section?.blocks)
      ? {
          ...section,
          blocks: section.blocks.map((block) =>
            block && typeof block === "object"
              ? fillBlock(
                  fromLegacyBlock(block as Record<string, unknown>) as Parameters<typeof fillBlock>[0],
                  (key) => byKey.get(key)?.value,
                  (cell) => numberInCell(cell) !== null,
                  (key) => byKey.get(key)?.unit,
                  (key) => byKey.get(key)?.dp
                )
              : block
          )
        }
      : section
  );
  return JSON.stringify(filled);
};

type TSlotHolder = { refs?: { format?: unknown }[]; items?: { format?: { at?: unknown } }[] };

export const hasEmptySlots = (content: string | null | undefined): boolean =>
  (sectionsOf(content ?? "") ?? []).some(
    (section) =>
      Array.isArray(section?.blocks) &&
      section.blocks.some((raw) => {
        const block = (raw && typeof raw === "object" ? raw : {}) as TSlotHolder;
        return (
          (Array.isArray(block.refs) && block.refs.some((ref) => !!ref?.format)) ||
          (Array.isArray(block.items) && block.items.some((item) => item?.format?.at !== undefined))
        );
      })
  );

export const headlineDisplay = (item: THeadlineItem, figure: TFigureValue | undefined): string => {
  const shown = (value: string) => withTileUnit(value, item.unit);
  if (figure && figure.value !== null) {
    if (item.value && /\d/.test(item.value)) return shown(item.value);
    const unit = figure.unit ?? item.unit;
    return spellLedgerValue(figure.value, unit, figure.dp);
  }
  return item.value ? shown(item.value) : "—";
};
